import _ from "lodash";
import {
    bandChildrenAsArray,
    bandSelectionKey,
    inferExecutiveSummaryHierarchyBandType,
    withInferredHierarchyBandType
} from "./executiveSummarySelectionUtils";

const ES_ENTITIES = ["project", "assets", "energy_mng"];
const TEXT_SYNC_FIELDS = ["name", "text"];
const IMAGE_SYNC_FIELDS = ["name", "data", "config"];

const sanitizeMemoText = text =>
    typeof text === "string"
        ? text
              .replace(/(<\w+>)\s+/g, "$1")
              .replace(/\s+(<\/\w+>)/g, "$1")
              .trim()
        : text;

export function mergeExecutiveSummaryItemsFromApiBandTilesResponse(items) {
    if (!Array.isArray(items) || !items.length) return {};
    return Object.assign({}, ...items.map(tile => tile?.executive_summary || {}));
}
export function buildBandIdIndexFromMergedExecutiveSummary(data) {
    const map = new Map();

    function traverse(node) {
        if (!node || typeof node !== "object") return;
        if (node.band_id != null && String(node.band_id) !== "") {
            map.set(String(node.band_id), node);
        }

        const children = node.band;
        if (Array.isArray(children)) {
            children.forEach(traverse);
        } else if (children && typeof children === "object") {
            Object.values(children).forEach(traverse);
        }
    }

    Object.values(data || {}).forEach(traverse);
    return map;
}

function buildBandIdKeyIndexFromMergedExecutiveSummary(mergedExecutiveSummary) {
    const map = new Map();
    function walk(node, apiKey) {
        if (!node || typeof node !== "object") return;
        if (node.band_id != null && String(node.band_id) !== "" && apiKey) {
            map.set(String(node.band_id), apiKey);
        }
        const b = node.band;
        if (!b) return;
        if (Array.isArray(b)) {
            b.forEach(child => walk(child, child?.apiKey));
            return;
        }
        Object.entries(b).forEach(([childKey, child]) => walk(child, childKey));
    }
    if (!mergedExecutiveSummary || typeof mergedExecutiveSummary !== "object") return map;
    Object.entries(mergedExecutiveSummary).forEach(([rootKey, root]) => walk(root, rootKey));
    return map;
}

function stableBandChildMatchKey(node) {
    if (!node || typeof node !== "object") return "";
    const id = bandSelectionKey(node);
    return id ? `id:${id}` : "";
}

function backendKeyForBandId(baselineKeyByBandId, node) {
    const bandId = node?.band_id != null && String(node.band_id) !== "" ? String(node.band_id) : "";
    return bandId ? baselineKeyByBandId?.get(bandId) || "" : "";
}

function collectChangedFields(before = {}, after = {}, fields = []) {
    const changed = {};
    for (const field of fields) {
        if (field === "text") {
            const beforeText = sanitizeMemoText(before[field]);
            const afterText = sanitizeMemoText(after[field]);
            if (!_.isEqual(beforeText, afterText)) {
                changed[field] = afterText;
            }
            continue;
        }
        if (!_.isEqual(before[field], after[field])) {
            changed[field] = after[field];
        }
    }
    return changed;
}

function attachBandIdentity(result, chartNode) {
    if (!result || !chartNode) return null;
    const bandId = chartNode?.band_id;
    if (bandId !== undefined && bandId !== null && String(bandId) !== "") {
        result.band_id = bandId;
    }
    result.type = chartNode?.type;
    return result;
}

function nodeHasChildren(node) {
    const band = node?.band;
    return Array.isArray(band) ? band.length > 0 : !!(band && typeof band === "object" && Object.keys(band).length);
}

function syncTypeForNode(node) {
    if (node?.band_type === "group" || nodeHasChildren(node)) return "group";
    if (node?.band_type === "text" || typeof node?.text === "string") return "text";
    if (node?.band_type === "image" || Array.isArray(node?.data)) return "image";
    return inferExecutiveSummaryHierarchyBandType(node) || "group";
}

function diffTextLeafVsBaseline(baselineNode, chartNode) {
    const baselineN = baselineNode || {};
    const chartN = chartNode || {};
    const result = collectChangedFields(baselineN, chartN, TEXT_SYNC_FIELDS);
    if (!Object.keys(result).length) return null;
    return attachBandIdentity(result, chartN);
}

function diffImageLeafVsBaseline(baselineNode, chartNode) {
    const baselineN = baselineNode || {};
    const chartN = chartNode || {};
    const result = collectChangedFields(baselineN, chartN, IMAGE_SYNC_FIELDS);
    if (!Object.keys(result).length) return null;
    return attachBandIdentity(result, chartN);
}

function diffBandRecordVsBaseline(baselineByBandId, baselineKeyByBandId, parentGroupNode) {
    const afterKids = bandChildrenAsArray(parentGroupNode?.band);
    const out = {};
    for (const child of afterKids) {
        const patch = diffHierarchyNodeVsBaseline(baselineByBandId, baselineKeyByBandId, child);
        if (!patch) continue;
        const recordKey = backendKeyForBandId(baselineKeyByBandId, child) || stableBandChildMatchKey(child) || bandSelectionKey(child) || "";
        if (!recordKey) continue;
        out[recordKey] = patch;
    }
    return Object.keys(out).length ? out : null;
}

function diffGroupNodeVsBaseline(baselineByBandId, baselineKeyByBandId, afterN) {
    const bandId = afterN.band_id != null && String(afterN.band_id) !== "" ? String(afterN.band_id) : "";
    const beforeN = bandId ? baselineByBandId.get(bandId) : undefined;
    const parentType = syncTypeForNode(afterN);
    const parentDelta =
        parentType === "text"
            ? collectChangedFields(beforeN || {}, afterN, TEXT_SYNC_FIELDS)
            : parentType === "image"
              ? collectChangedFields(beforeN || {}, afterN, IMAGE_SYNC_FIELDS)
              : {};
    const bandPart = diffBandRecordVsBaseline(baselineByBandId, baselineKeyByBandId, afterN);
    if (!bandPart && !Object.keys(parentDelta).length) return null;
    const merged = { ...parentDelta };
    if (bandPart) {
        merged.band = bandPart;
    }
    attachBandIdentity(merged, afterN);
    return merged;
}

function diffHierarchyNodeVsBaseline(baselineByBandId, baselineKeyByBandId, afterNode) {
    const afterN = withInferredHierarchyBandType(afterNode || {});
    const bandId = afterN.band_id != null && String(afterN.band_id) !== "" ? String(afterN.band_id) : "";
    const beforeN = bandId ? baselineByBandId.get(bandId) : undefined;
    const bt = afterN.band_type || inferExecutiveSummaryHierarchyBandType(afterN);

    if (nodeHasChildren(afterN)) {
        return diffGroupNodeVsBaseline(baselineByBandId, baselineKeyByBandId, afterN);
    }

    if (bt === "text") {
        return diffTextLeafVsBaseline(beforeN, afterN);
    }
    if (bt === "image") {
        return diffImageLeafVsBaseline(beforeN, afterN);
    }
    if (bt === "group") {
        return diffGroupNodeVsBaseline(baselineByBandId, baselineKeyByBandId, afterN);
    }
    return null;
}

/**
 * True if this chart tile is an Executive Summary hierarchy tile (same flags as persisted on the tile).
 * @param {Record<string, any>|null|undefined} tile
 * @param {string} chartKey
 */
export function isExecutiveSummaryHierarchyChartTile(tile, chartKey) {
    return !!tile?.is_building_exec_sum;
}

export function diffExecSumHierarchyTileVsBuildingBaseline(baselineByBandId, baselineKeyByBandId, currentTile, chartKey = "") {
    if (!isExecutiveSummaryHierarchyChartTile(currentTile, chartKey)) return null;
    const cur = currentTile;
    const rootBandId = cur.band_id != null && String(cur.band_id) !== "" ? String(cur.band_id) : "";
    const rootBase = rootBandId ? baselineByBandId.get(rootBandId) : undefined;
    const rootType = cur.band_type || inferExecutiveSummaryHierarchyBandType(cur);
    let tile_delta = null;

    if (nodeHasChildren(cur)) {
        tile_delta = diffGroupNodeVsBaseline(baselineByBandId, baselineKeyByBandId, cur);
    } else if (rootType === "text") {
        tile_delta = diffTextLeafVsBaseline(rootBase, cur);
    } else if (rootType === "image") {
        tile_delta = diffImageLeafVsBaseline(rootBase, cur);
    } else if (rootType === "group") {
        const bandDelta = diffBandRecordVsBaseline(baselineByBandId, baselineKeyByBandId, cur);
        if (bandDelta) {
            tile_delta = attachBandIdentity({ band: bandDelta }, cur);
        }
    }

    return tile_delta && Object.keys(tile_delta).length ? tile_delta : null;
}

export function collectExecutiveSummaryBuildingSyncDeltas(mergedBaselineExecutiveSummary, currentPropertiesText) {
    if (
        !mergedBaselineExecutiveSummary ||
        typeof mergedBaselineExecutiveSummary !== "object" ||
        !Object.keys(mergedBaselineExecutiveSummary).length
    ) {
        return {};
    }
    if (!currentPropertiesText) return {};

    const baselineByBandId = buildBandIdIndexFromMergedExecutiveSummary(mergedBaselineExecutiveSummary);
    const baselineKeyByBandId = buildBandIdKeyIndexFromMergedExecutiveSummary(mergedBaselineExecutiveSummary);
    const tiles = {};

    for (const entity of ES_ENTITIES) {
        const curTypes = currentPropertiesText?.[entity]?.band1?.type || {};
        for (const chartKey of Object.keys(curTypes)) {
            const curTile = curTypes[chartKey];

            if (!isExecutiveSummaryHierarchyChartTile(curTile, chartKey)) continue;
            const tile_delta = diffExecSumHierarchyTileVsBuildingBaseline(baselineByBandId, baselineKeyByBandId, curTile, chartKey);
            if (!tile_delta) continue;
            tiles[backendKeyForBandId(baselineKeyByBandId, curTile) || chartKey] = tile_delta;
        }
    }
    return tiles;
}
